import { useWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Key, useState, useEffect } from 'react';
import { ColumnsType } from '../Table';
import { ListWorkOrdersResult } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { CheckOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import InfiniteTable from '../InfiniteTable';
import { ModalProps } from '@/types/ModalProps';

interface WorkOrderModalProps extends ModalProps {
  finishCallback?: (workorder?: ListWorkOrdersResult[]) => void;
  selectedWorkOrder?: number[];
  onWorkOrderSelect?: (workorder: ListWorkOrdersResult) => void;
}

export default function WorkOrderSelectModal({
  visible,
  handleVisible,
  finishCallback,
  selectedWorkOrder,
  onWorkOrderSelect,
}: WorkOrderModalProps) {
  const [selectWorkOrderKeys, setSelectWorkOrderKeys] = useState<Key[]>();
  const { data, isFetching, hasNextPage, fetchNextPage } = useWorkOrders({
    order: 1,
  });
  const workOrderData = flatInfiniteData(data);

  const handleSelectWorkOrder = (selectedRowKeys: Key[]) => {
    setSelectWorkOrderKeys(selectedRowKeys);
  };

  const getSelectedWorkOrder = () => {
    const result = workOrderData.filter(({ complementId }) =>
      selectWorkOrderKeys?.includes(complementId),
    );
    return result;
  };

  const handleCancel = () => {
    setSelectWorkOrderKeys([]);
    handleVisible();
  };

  const handleOk = () => {
    const workorder = getSelectedWorkOrder();
    if (finishCallback) {
      finishCallback(workorder);
    }
    handleCancel();
  };

  useEffect(() => {
    if (visible) {
      setSelectWorkOrderKeys(selectedWorkOrder);
    }
  }, [visible]);

  const columns: ColumnsType<ListWorkOrdersResult> = [
    { dataIndex: 'complementIdentifier', title: 'Orçamento', width: '10%' },
    {
      dataIndex: ['customer', 'name'],
      title: 'Cliente',
      ellipsis: true,
      width: onWorkOrderSelect ? '30%' : '50%',
    },
    {
      dataIndex: ['insurer', 'name'],
      title: 'Seguradora',
      ellipsis: true,
      width: '20%',
    },
    { dataIndex: 'licensePlate', title: 'Placa', width: '10%' },
    {
      dataIndex: 'status',
      title: 'Situação',
      width: '10%',
      render: (_, { status }) =>
        status.includes('open')
          ? 'Aberto'
          : status.includes('authorized')
          ? 'Autorizado'
          : status.includes('closed')
          ? 'Fechado'
          : 'Cancelado',
    },
    onWorkOrderSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onWorkOrderSelect(row)}
              icon={<CheckOutlined />}
            ></Button>
          ),
          width: '20%',
        }
      : {},
  ];

  return (
    <Modal
      visible={visible}
      onCancel={handleVisible}
      onOk={handleOk}
      title="Selecionar Orçamento"
      width={1000}
    >
      <InfiniteTable
        dataSource={workOrderData}
        columns={columns}
        loading={isFetching}
        rowKey={(row) => row.complementId}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectWorkOrderKeys,
          onChange: handleSelectWorkOrder,
        }}
      />
    </Modal>
  );
}
